import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import { FaChevronDown } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import ProductService from "../../services/product";
import CategoryService from "../../services/category";
import ProductCard from "../../components/ProductCard";
import "./index.scss";
import Header from "../../components/appLayout/header";
 
const AllProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedProductType, setSelectedProductType] = useState(null);

  const [searchParams] = useSearchParams();

  const productService = new ProductService();
  const categoryService = new CategoryService();
 
  useEffect(() => {
    loadData();
  }, []);
 
  useEffect(() => {
    const categoryParam = searchParams.get("category");
    const typeParam = searchParams.get("type");
 
    if (categoryParam && categoryParam !== "all") {
      setSelectedCategory(categoryParam);
      setSelectedProductType(null);
    } else if (typeParam) {
      setSelectedProductType(typeParam);
      setSelectedCategory("all");
    }
  }, [searchParams, categories, products]);
  useEffect(() => {
    filterProducts();
  }, [products, selectedCategory, selectedProductType, sortBy]);
 
  const loadData = async () => {
    try {
      setLoading(true);
      const [productsRes, categoriesRes] = await Promise.all([
        productService.getAllProducts(),
        categoryService.getAllCategories(),
      ]);
 
      setProducts(productsRes?.data || []);
      const categoriesArray = Array.isArray(categoriesRes?.data)
        ? categoriesRes.data
        : [];
      setCategories(categoriesArray.filter((cat) => cat && cat.title));
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };
 
  const filterProducts = () => {
    let filtered = [...products];
    // Filtrage par catégorie
    if (selectedCategory && selectedCategory !== "all") {
      filtered = filtered.filter(
        (product) =>
          product.categoryId?._id === selectedCategory ||
          product.categoryId === selectedCategory
      );
    }
    // Filtrage par type de produit
    if (selectedProductType) {
      switch (selectedProductType) {
        case "bestSeller":
          filtered = filtered.filter((p) => p.isBestSeller);
          break;
        case "bientotDispo":
          filtered = filtered.filter((p) => p.isBientotDispo);
          break;
        case "nouveaux":
          filtered = filtered.filter((p) => p.isNouveaux);
          break;
        case "promotions":
          const now = new Date();
          filtered = filtered.filter((p) => {
            if (!p.isOnPromo) return false;
            const start = p.promoStartDate ? new Date(p.promoStartDate) : null;
            const end = p.promoEndDate ? new Date(p.promoEndDate) : null;
            return start && end && now >= start && now <= end;
          });
          break;
        default:
          break;
      }
    }
    // Tri
    if (sortBy) {
      switch (sortBy) {
        case "price-asc":
          filtered.sort((a, b) => (a.ttc || 0) - (b.ttc || 0));
          break;
        case "price-desc":
          filtered.sort((a, b) => (b.ttc || 0) - (a.ttc || 0));
          break;
        case "name-asc":
          filtered.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
          break;
        case "name-desc":
          filtered.sort((a, b) => (b.title || "").localeCompare(a.title || ""));
          break;
        default:
          break;
      }
    }
 
    setFilteredProducts(filtered);
  };
 
  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(selectedCategory === categoryId ? "all" : categoryId);
    setSelectedProductType(null);
  };
 
  const handleProductTypeClick = (type) => {
    setSelectedProductType(selectedProductType === type ? null : type);
    setSelectedCategory("all");
  };
 
  const clearAllFilters = () => {
    setSelectedCategory("all");
    setSelectedProductType(null);
    setSortBy("");
  };
 
  const getProductCount = (filterType, filterValue) => {
    if (filterType === "category") {
      return products.filter(
        (product) =>
          product.categoryId?._id === filterValue ||
          product.categoryId === filterValue
      ).length;
    }
    if (filterType === "productType") {
      switch (filterValue) {
        case "bestSeller":
          return products.filter((product) => product.isBestSeller).length;
        case "bientotDispo":
          return products.filter((product) => product.isBientotDispo).length;
        case "nouveaux":
          return products.filter((product) => product.isNouveaux).length;
        case "promotions":
          const now = new Date();
          return products.filter((p) => {
            if (!p.isOnPromo) return false;
            const start = p.promoStartDate ? new Date(p.promoStartDate) : null;
            const end = p.promoEndDate ? new Date(p.promoEndDate) : null;
            return start && end && now >= start && now <= end;
          }).length;
        default:
          return 0;
      }
    }
    return 0;
  };
 
  if (loading) {
    return (
      <div className="all-products-page">
        <Container>
          <div className="text-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }
 
  return (
    <div>
      <div className="p-md-5 p-0 pt-md-3">
        <Header />
      </div>
      <div className="all-products-page">
        <Container>
          <Row>
            <Col xs={12}>
              <div className="page-header">
                <h1>TOUS LES PRODUITS</h1>
                <p className="page-description">
                  Découvrez notre gamme complète de soins frais, efficaces et
                  stimulants pour le microbiome, sans additifs inutiles. Des
                  soins frais pour une peau visiblement plus douce.
                </p>
              </div>
            </Col>
          </Row>
 
          <Row>
            <Col lg={3} md={4} className="sidebar">
              <div className="filters-container">
                <div className="filter-section">
                  <h6>Trier par</h6>
                  <Form.Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="sort-select">
                    <option value="">Trier par</option>
                    <option value="name-asc">Nom (A-Z)</option>
                    <option value="name-desc">Nom (Z-A)</option>
                    <option value="price-asc">Prix (croissant)</option>
                    <option value="price-desc">Prix (décroissant)</option>
                  </Form.Select>
                </div>
 
                <div className="filter-section">
                  <div
                    className={`filter-item ${
                      selectedCategory === "all" || selectedCategory === null
                        ? "active"
                        : ""
                    }`}
                    onClick={() => handleCategoryClick("all")}>
                    <span>TOUS LES PRODUITS ({products.length})</span>
                    <FaChevronDown size={12} />
                  </div>
 
                  {[
                    { type: "bientotDispo", label: "Bientôt disponible" },
                    { type: "nouveaux", label: "Nouveautés" },
                    { type: "bestSeller", label: "Best Sellers" },
                    { type: "promotions", label: "Promotions" },
                  ].map((item) => (
                    <div
                      key={item.type}
                      className={`filter-item ${
                        selectedProductType === item.type ? "active" : ""
                      }`}
                      onClick={() => handleProductTypeClick(item.type)}>
                      <span>
                        {item.label} (
                        {getProductCount("productType", item.type)})
                      </span>
                      <FaChevronDown size={12} />
                    </div>
                  ))}
                  {categories.map((category) => (
                    <div
                      key={category._id}
                      className={`filter-item ${
                        selectedCategory === category._id ? "active" : ""
                      }`}
                      onClick={() => handleCategoryClick(category._id)}>
                      <span>
                        {category.title?.toUpperCase() || "UNKNOWN"} (
                        {getProductCount("category", category._id)})
                      </span>
                      <FaChevronDown size={12} />
                    </div>
                  ))}
                </div>
              </div>
            </Col>
 
            <Col lg={9} md={8}>
              <div className="products-grid">
                <Row>
                  {filteredProducts.map((product) => (
                    <Col lg={4} md={6} sm={6} xs={12} key={product._id}>
                      <ProductCard product={product} />
                    </Col>
                  ))}
                </Row>
 
                {filteredProducts.length === 0 && (
                  <div className="no-products">
                    <p>Aucun produit trouvé avec les filtres sélectionnés.</p>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
};
 
export default AllProductsPage;